// src/pages/Consultation/modals/PauseSessionModal.jsx
import React, { useState } from "react";
import { useDispatch } from "react-redux";
import { FiX, FiPause, FiClock } from "react-icons/fi";
import toast from "react-hot-toast";
import Button from "../../../../components/common/Button";
import { pauseSessionThunk } from "../../../../store/thunks/consultationThunks";

const PAUSE_REASONS = [
  { value: "CUSTOMER_REQUEST", label: "Customer Requested Break" },
  { value: "LUNCH_BREAK", label: "Lunch Break" },
  { value: "TEA_BREAK", label: "Tea/Coffee Break" },
  { value: "RESTROOM", label: "Restroom Break" },
  { value: "PHONE_CALL", label: "Customer Phone Call" },
  { value: "TECHNICAL_ISSUE", label: "Technical Issue" },
  { value: "CUSTOMER_NEEDS_TIME", label: "Customer Needs Time to Think" },
  { value: "AWAITING_APPROVAL", label: "Awaiting Manager Approval" },
  { value: "STOCK_CHECK", label: "Checking Stock/Availability" },
  { value: "OTHER", label: "Other Reason" },
];

const DURATION_OPTIONS = [
  { value: 5, label: "5 minutes" },
  { value: 10, label: "10 minutes" },
  { value: 15, label: "15 minutes" },
  { value: 30, label: "30 minutes" },
  { value: 45, label: "45 minutes" },
  { value: 60, label: "1 hour" },
];

const PauseSessionModal = ({ isOpen, onClose, session, adminInfo }) => {
  const dispatch = useDispatch();

  const [formData, setFormData] = useState({
    reason: "",
    customReason: "",
    estimatedDuration: 15,
    notes: "",
  });

  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  // Handle field changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));

    // Clear error for this field
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  // Validate form
  const validateForm = () => {
    const newErrors = {};

    if (!formData.reason) {
      newErrors.reason = "Please select a reason for pausing";
    }

    if (formData.reason === "OTHER" && !formData.customReason.trim()) {
      newErrors.customReason = "Please specify the reason";
    }

    if (!formData.estimatedDuration) {
      newErrors.estimatedDuration = "Please select estimated duration";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle submit
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      toast.error("Please fix the errors in the form");
      return;
    }

    setSubmitting(true);

    try {
      const pauseData = {
        sessionId: session._id,
        reason:
          formData.reason === "OTHER"
            ? formData.customReason
            : PAUSE_REASONS.find((r) => r.value === formData.reason)?.label ||
              formData.reason,
        adminCode: adminInfo.adminCode,
        adminName: adminInfo.adminName,
      };

      await dispatch(pauseSessionThunk(pauseData)).unwrap();

      toast.success(`Session paused for ${formData.estimatedDuration} minutes`);
      handleClose();
    } catch (error) {
      console.error("Pause session error:", error);
      toast.error(error || "Failed to pause session");
    } finally {
      setSubmitting(false);
    }
  };

  // Handle close
  const handleClose = () => {
    if (!submitting) {
      setFormData({
        reason: "",
        customReason: "",
        estimatedDuration: 15,
        notes: "",
      });
      setErrors({});
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
        onClick={handleClose}
      />

      {/* Modal */}
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="relative w-full max-w-lg bg-white rounded-lg shadow-xl">
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-warning-light rounded-lg">
                <FiPause className="text-xl text-warning-dark" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900">
                  Pause Session
                </h3>
                <p className="text-sm text-gray-500">
                  Session: {session?.sessionCode}
                </p>
              </div>
            </div>
            <button
              onClick={handleClose}
              disabled={submitting}
              className="p-1 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50"
            >
              <FiX className="text-xl text-gray-500" />
            </button>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="p-6 space-y-5">
            {/* Pause Reason */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Reason for Pause *
              </label>
              <select
                name="reason"
                value={formData.reason}
                onChange={handleChange}
                disabled={submitting}
                className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary ${
                  errors.reason ? "border-red-500" : "border-gray-300"
                }`}
              >
                <option value="">Select a reason</option>
                {PAUSE_REASONS.map((reason) => (
                  <option key={reason.value} value={reason.value}>
                    {reason.label}
                  </option>
                ))}
              </select>
              {errors.reason && (
                <p className="mt-1 text-sm text-red-500">{errors.reason}</p>
              )}
            </div>

            {/* Custom Reason (if OTHER selected) */}
            {formData.reason === "OTHER" && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Specify Reason *
                </label>
                <input
                  type="text"
                  name="customReason"
                  value={formData.customReason}
                  onChange={handleChange}
                  disabled={submitting}
                  placeholder="Enter the reason for pausing"
                  className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary ${
                    errors.customReason ? "border-red-500" : "border-gray-300"
                  }`}
                />
                {errors.customReason && (
                  <p className="mt-1 text-sm text-red-500">
                    {errors.customReason}
                  </p>
                )}
              </div>
            )}

            {/* Estimated Duration */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <FiClock className="inline mr-1" />
                Estimated Duration *
              </label>
              <div className="grid grid-cols-3 gap-2">
                {DURATION_OPTIONS.map((option) => (
                  <button
                    key={option.value}
                    type="button"
                    onClick={() =>
                      setFormData((prev) => ({
                        ...prev,
                        estimatedDuration: option.value,
                      }))
                    }
                    disabled={submitting}
                    className={`px-3 py-2 text-sm rounded-lg border transition-all ${
                      formData.estimatedDuration === option.value
                        ? "bg-primary text-white border-primary"
                        : "bg-white text-gray-700 border-gray-300 hover:border-primary"
                    } disabled:opacity-50`}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
              {errors.estimatedDuration && (
                <p className="mt-1 text-sm text-red-500">
                  {errors.estimatedDuration}
                </p>
              )}
            </div>

            {/* Notes */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Additional Notes (Optional)
              </label>
              <textarea
                name="notes"
                value={formData.notes}
                onChange={handleChange}
                disabled={submitting}
                rows={3}
                placeholder="Any additional information..."
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary resize-none"
              />
            </div>

            {/* Info Box */}
            <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
              <p className="text-sm text-yellow-800">
                The session timer will continue to track total duration. You can
                resume the session at any time.
              </p>
            </div>
          </form>

          {/* Footer Actions */}
          <div className="flex items-center justify-end gap-3 px-6 py-4 bg-gray-50 border-t rounded-b-lg">
            <Button
              type="button"
              variant="outline"
              onClick={handleClose}
              disabled={submitting}
            >
              Cancel
            </Button>
            <Button
              type="button"
              onClick={handleSubmit}
              loading={submitting}
              disabled={submitting}
              className="bg-warning hover:bg-warning-dark"
            >
              {submitting ? "Pausing..." : "Pause Session"}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PauseSessionModal;
